import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Image from 'src/components/image';
import Iconify from 'src/components/iconify/iconify';
import PropTypes from 'prop-types';
import ResumeViewer from './resume-viewer';
import { getFormattedDateWithSlash } from './utility';
import { SCREENING_ERROR } from '../job-candidate/job-candidate-status-constants';
import FallbackUI from './fallback-ui';
import DomainPieChart from './pie-chart';
import ProfileLoader from './loader';
import NoResumeFound from './no-resume-found';
import { CandidateProfileContext } from './context/candidate-profile-context';
import Notes from './notes';

const useStyles = makeStyles(theme => ({
  mainDiv: {
    gap: 20,
  },
  elementsDiv: {
    paddingTop: '16px',
    gap: 12,
  },
  blueTagsDiv: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  blueTagsDivItem: {
    borderRadius: 4,
    padding: '4px 8px',
    background: theme.palette.background.primaryAccent2,
    color: theme.palette.content.accent1,
    margin: '0px 8px 8px 0px',
  },
  ExperienceItemDiv: {
    gap: 8,
    padding: '16px 12px',
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    borderRadius: 6,
  },
  workDuration: {
    padding: '4px 8px',
    borderRadius: 21,
    background: theme.palette.background.primaryGrey2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  disqualifiedDiv: {
    flexDirection: 'row',
    padding: '11.5px 16px',
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
    alignItems: 'center',
    gap: '10px',
  },
}));

const ExperienceItem = ({ item }) => {
  const classes = useStyles();
  const workTags = item?.work_tags?.split(',');
  const startDate = getFormattedDateWithSlash(item?.start_date);
  const endDate = getFormattedDateWithSlash(item?.end_date);

  return (
    <Stack className={classes.ExperienceItemDiv}>
      <Typography
        variant="paragraph"
        sx={{ fontWeight: 600, marginBottom: '4px' }}
      >
        {item?.job_role}
      </Typography>
      <Stack spacing="4px" direction="row" alignItems="center">
        <Typography variant="caption">{item?.company_name}</Typography>
        {startDate && endDate && (
          <>
            <Typography variant="caption">&bull;</Typography>
            <Typography variant="caption">
              {startDate}-{endDate}
            </Typography>
          </>
        )}
        {item?.work_duration &&
          !item?.work_duration?.toLowerCase().includes('not') && (
            <>
              <Typography variant="caption">&bull;</Typography>
              <Stack className={classes.workDuration}>
                <Typography variant="caption">{item?.work_duration}</Typography>
              </Stack>
            </>
          )}
      </Stack>
      <Typography variant="caption" sx={{ lineHeight: 'normal' }}>
        {item?.work_summary}
      </Typography>
      <Stack className={classes.blueTagsDiv}>
        {workTags?.map((tag, ind) => (
          <Stack key={ind} className={classes.blueTagsDivItem}>
            <Typography variant="input" sx={{ fontWeight: 500, fontSize: 12 }}>
              #{tag}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
const EducationItem = ({ item }) => {
  const classes = useStyles();
  return (
    <Stack className={classes.ExperienceItemDiv}>
      {item?.degree && (
        <Typography
          variant="paragraph"
          sx={{ fontWeight: 600, marginBottom: '4px' }}
        >
          {item?.degree}
        </Typography>
      )}
      <Stack spacing="4px" direction="row">
        {item?.institution && (
          <Typography variant="caption">{item?.institution}</Typography>
        )}
        {item?.start_time && item?.end_time && (
          <>
            <Typography variant="caption">&bull;</Typography>
            <Typography variant="caption">
              {item?.start_time}-{item?.end_time}
            </Typography>
          </>
        )}
      </Stack>
      {item?.summary && (
        <Typography variant="caption" sx={{ lineHeight: 'normal' }}>
          {item?.summary}
        </Typography>
      )}
    </Stack>
  );
};
const chartColors = [
  '#002D69',
  '#2F9FF6',
  '#212121',
  '#3357FF',
  '#383838',
  '#5418EF',
  '#08944A',
];

const TABS={
  PROFILE:{
    label: 'Profile',
    value: 'PROFILE',
  },
  NOTES:{
    label: 'Notes',
    value: 'NOTES',
  }
}

const ProfileGroup = ({ jobId }) => {
  // hooks
  const classes = useStyles();
  const candidateDetails = useContext(CandidateProfileContext)?.obj;
  const isLoading = useContext(CandidateProfileContext)?.isLoading;

  // memo
  const resumeUrl = useMemo(
    () => candidateDetails?.candidate_info?.resume_url,
    [candidateDetails?.candidate_info?.resume_url],
  );

  // states
  const [profile, setProfile] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(TABS.PROFILE.value);

  // variables
  const jobCandidateStatus = candidateDetails?.screening_status;
  const personality = profile?.user_persona?.personality;
  const skills = profile?.user_persona?.skills;
  const summary = candidateDetails?.candidate_info?.profile_blob
    ?.split('\n\n')[4]
    .slice(8);
  const experiences = Object.values(
    profile === null || Object.keys(profile).length === 0
      ? {}
      : profile?.experience,
  );
  const education = Object.values(
    profile === null || Object.keys(profile).length === 0
      ? {}
      : profile?.education,
  );
  const chartData = profile?.user_persona?.domain_list?.map((item, index) => {
    let label = `${item?.domain_name} ${item?.total_exp}`;

    if (item?.total_exp === 1) label += ' year';
    else label += ' years';

    const obj = {
      id: index,
      value: item?.total_exp,
      name: label,
      fill: chartColors[index],
    };

    return obj;
  });

  const downloadResume = async _resumeUrl => {
    if (_resumeUrl) {
      window.open(_resumeUrl, '_blank');
    }
  };

  // effects
  useEffect(() => {
    if (candidateDetails?.candidate_info?.profile_json) {
      setProfile(JSON.parse(candidateDetails?.candidate_info?.profile_json));
    }
    if (!isLoading) {
      setIsProfileLoading(false);
    }
  }, [candidateDetails, isLoading]);

  const ProfileGroupSkeleton = (
    <Stack className={classes.mainDiv}>
      <Stack className={classes.elementsDiv}>
        <Skeleton width={102} height={10} />
        <Stack className={classes.blueTagsDiv} spacing="8px">
          <Skeleton width={118} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={77} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={118} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={141} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={89} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={119} height={27} sx={{ borderRadius: '4px' }} />
        </Stack>
      </Stack>
      <Stack className={classes.elementsDiv}>
        <Skeleton width={47} height={10} />
        <Stack className={classes.blueTagsDiv} spacing="8px">
          <Skeleton width={118} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={118} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={160} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={127} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={107} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={45} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={102} height={27} sx={{ borderRadius: '4px' }} />
        </Stack>
      </Stack>
      <Stack className={classes.elementsDiv}>
        <Skeleton width={87} height={10} />
        <Stack className={classes.blueTagsDiv} spacing="8px">
          <Skeleton width={152} height={27} sx={{ borderRadius: '4px' }} />
          <Skeleton width={135} height={27} sx={{ borderRadius: '4px' }} />
        </Stack>
      </Stack>
      <Stack className={classes.elementsDiv}>
        <Skeleton width={87} height={10} />
        <Stack spacing="11px">
          <Skeleton width="100%" height={10} />
          <Skeleton width="40%" height={10} />
          <Skeleton width="75%" height={10} />
          <Skeleton width="60%" height={10} />
        </Stack>
      </Stack>
      <Stack className={classes.elementsDiv}>
        <Skeleton width={69} height={10} />
        <Stack
          width="100%"
          direction="row"
          alignItems="center"
          justifyContent="center"
          py="8px"
        >
          <Stack direction="row" alignItems="center" spacing="50px">
            <Skeleton variant="circular" width={150} height={150} />
            <Stack spacing="12px">
              <Stack direction="row" alignItems="center" spacing="8px">
                <Skeleton
                  variant="rectangular"
                  width={12}
                  height={12}
                  sx={{
                    backgroundColor: '#919191',
                  }}
                />
                <Skeleton width={49} height={8} />
                <Skeleton width={46} height={8} />
              </Stack>
              <Stack direction="row" alignItems="center" spacing="8px">
                <Skeleton variant="rectangular" width={12} height={12} />
                <Skeleton width={77} height={8} />
                <Skeleton width={46} height={8} />
              </Stack>
              <Stack direction="row" alignItems="center" spacing="8px">
                <Skeleton variant="rectangular" width={12} height={12} />
                <Skeleton width={132} height={8} />
                <Skeleton width={37} height={8} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack className={classes.elementsDiv}>
        <Skeleton width={108} height={10} />
        <Stack spacing="16px">
          <Stack
            sx={{
              borderRadius: '6px',
              border: '1px solid #E8E8E8',
              padding: '22px 12px',
            }}
          >
            <Skeleton width={154} height={10} sx={{ mb: '15px' }} />
            <Stack
              direction="row"
              alignItems="center"
              spacing="4px"
              sx={{ mb: '21px' }}
            >
              <Skeleton width={60} height={10} />
              <Skeleton width={18} height={10} />
              <Skeleton width={94} height={10} />
              <Skeleton width={18} height={10} />
              <Skeleton width={14} height={24} sx={{ borderRadius: '21px' }} />
            </Stack>
            <Skeleton width="100%" height={10} sx={{ mb: '12px' }} />
            <Stack direction="row" spacing="11px" alignItems="center">
              <Skeleton width={118} height={24} sx={{ borderRadius: '4px' }} />
              <Skeleton width={92} height={24} sx={{ borderRadius: '4px' }} />
              <Skeleton width={115} height={24} sx={{ borderRadius: '4px' }} />
            </Stack>
          </Stack>
          <Stack
            sx={{
              borderRadius: '6px',
              border: '1px solid #E8E8E8',
              padding: '22px 12px',
            }}
          >
            <Skeleton width={154} height={10} sx={{ mb: '15px' }} />
            <Stack
              direction="row"
              alignItems="center"
              spacing="4px"
              sx={{ mb: '21px' }}
            >
              <Skeleton width={60} height={10} />
              <Skeleton width={18} height={10} />
              <Skeleton width={94} height={10} />
              <Skeleton width={18} height={10} />
              <Skeleton width={14} height={24} sx={{ borderRadius: '21px' }} />
            </Stack>
            <Skeleton width="100%" height={10} sx={{ mb: '12px' }} />
            <Stack direction="row" spacing="11px" alignItems="center">
              <Skeleton width={118} height={24} sx={{ borderRadius: '4px' }} />
              <Skeleton width={92} height={24} sx={{ borderRadius: '4px' }} />
              <Skeleton width={115} height={24} sx={{ borderRadius: '4px' }} />
            </Stack>
          </Stack>
          <Stack
            sx={{
              borderRadius: '6px',
              border: '1px solid #E8E8E8',
              padding: '22px 12px',
            }}
          >
            <Skeleton width={154} height={10} sx={{ mb: '15px' }} />
            <Stack
              direction="row"
              alignItems="center"
              spacing="4px"
              sx={{ mb: '21px' }}
            >
              <Skeleton width={60} height={10} />
              <Skeleton width={18} height={10} />
              <Skeleton width={94} height={10} />
              <Skeleton width={18} height={10} />
              <Skeleton width={14} height={24} sx={{ borderRadius: '21px' }} />
            </Stack>
            <Skeleton width="100%" height={10} sx={{ mb: '12px' }} />
            <Stack direction="row" spacing="11px" alignItems="center">
              <Skeleton width={118} height={24} sx={{ borderRadius: '4px' }} />
              <Skeleton width={92} height={24} sx={{ borderRadius: '4px' }} />
              <Skeleton width={115} height={24} sx={{ borderRadius: '4px' }} />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <Stack spacing="20px">
      {isProfileLoading && ProfileGroupSkeleton}
      <Stack direction="row" alignItems="center" spacing="10px">
        {
          Object.keys(TABS).map((tab, index) => (
            <Stack
              key={index}
              onClick={() => setCurrentTab(TABS[tab].value)}
              sx={{
                cursor: 'pointer',
               border: '1px solid',
               borderColor: currentTab === TABS[tab].value ? 'border.primaryAccent1' : 'border.primaryBlack3',
                px: "68px",
                py:"13px",
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius:"42px",
                bgcolor: currentTab === TABS[tab].value ? "background.primaryAccent2" :"background.primaryWhite1"
              }}
            >
              <Typography
                variant="paragraph"
                sx={{
                  color: currentTab === TABS[tab].value ? 'content.accent1' : 'content.primaryBlack1',
                }}
              >
                {`${TABS[tab].label}${TABS[tab].value === TABS.NOTES.value ? ` ${candidateDetails?.candidate_notes_count || 0}` : ''}`}
              </Typography>
            </Stack>
          ))
        }
      </Stack>
      {
        currentTab === TABS.PROFILE.value &&
      <Stack>
      {!isProfileLoading &&
        candidateDetails &&
        Object.keys(candidateDetails).length !== 0 && (
          <>
            {profile && Object.keys(profile).length > 0 ? (
              <Stack className={classes.mainDiv}>
                {profile && personality && (
                  <Stack className={classes.elementsDiv}>
                    <Typography variant="subheading2">Personality</Typography>
                    <Stack className={classes.blueTagsDiv}>
                      {personality?.map((item, index) => (
                        <Stack key={index} className={classes.blueTagsDivItem}>
                          <Typography variant="input">#{item}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                )}
                {profile && skills && (
                  <Stack className={classes.elementsDiv}>
                    <Typography variant="subheading2">Skills</Typography>
                    <Stack className={classes.blueTagsDiv}>
                      {skills?.map((item, index) => (
                        <Stack key={index} className={classes.blueTagsDivItem}>
                          <Typography variant="input">{item}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                )}
                {summary && (
                  <Stack className={classes.elementsDiv}>
                    <Typography variant="subheading2">Summary</Typography>
                    <Typography variant="input" sx={{ lineHeight: 'normal' }}>
                      {summary}
                    </Typography>
                  </Stack>
                )}
                {chartData && chartData.length > 0 && (
                  <Stack className={classes.elementsDiv}>
                    <Typography variant="subheading2">Domain</Typography>
                    <DomainPieChart chartData={chartData} />
                  </Stack>
                )}
                {profile && experiences && experiences.length > 0 && (
                  <Stack className={classes.elementsDiv}>
                    <Typography variant="subheading2">Experience</Typography>
                    {experiences?.map((item, index) => (
                      <ExperienceItem key={index} item={item} />
                    ))}
                  </Stack>
                )}
                {profile && education && education.length > 0 && (
                  <Stack className={classes.elementsDiv}>
                    <Typography variant="subheading2">Education</Typography>
                    {education?.map((item, index) => (
                      <EducationItem key={index} item={item} />
                    ))}
                  </Stack>
                )}

                {jobCandidateStatus === SCREENING_ERROR && (
                  <Stack className={classes.disqualifiedDiv}>
                    <Image src="/assets/resume-error.svg" />
                    <Typography variant="subheading1">
                      Resume Parsing failed
                    </Typography>
                  </Stack>
                )}
              </Stack>
            ) : null}
            {resumeUrl ? (
              <Stack className={classes.elementsDiv}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subheading2" sx={{ flexGrow: 1 }}>
                    Resume
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => downloadResume(resumeUrl)}
                    //   size="small"
                    startIcon={<Iconify icon="tabler:download" />}
                  >
                    Download
                  </Button>
                </Stack>
                {resumeUrl && <ResumeViewer resumeUrl={resumeUrl} />}
              </Stack>
            ) : null}
            {!resumeUrl ? (
              <Stack>
                <NoResumeFound jobId={jobId} />
              </Stack>
            ) : null}
          </>
        )}
      </Stack>
}
{
  currentTab === TABS.NOTES.value &&
  <Stack>
    <Notes/>
  </Stack>
}
    </Stack>
  );
};

export default ProfileGroup;

ProfileGroup.propTypes = {
  jobId: PropTypes.string,
};

ExperienceItem.propTypes = {
  item: PropTypes.object.isRequired,
};

EducationItem.propTypes = {
  item: PropTypes.object.isRequired,
};
